import { useEffect, useState, type FC } from "react";
import { Modal, Form, Button, Divider, message } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { addUserAPI, updateUser } from "src/services/UserManagementAPIs";
import type { User, UserFormModalProps, UserFormValues } from "../Types/UserTypes";
import { useDashboardOptions } from "../Hooks/UseDashboardOptions";
import { GetApiErrorMessage, GetErrorMessage } from "../Utils/ErrorMessage";
import { ALL_DASHBOARDS_VALUE, SOCIAL_LISTENING_FEATURE } from "../Utils/UserConstants";
import UserFormFields from "./UserFormFields";

const ADD_INITIAL_VALUES: Partial<UserFormValues> = { role: "viewer", features: [] };

const MODE_TEXTS = {
  add: {
    title: "userManagement.modals.addUser.title",
    success: "userManagement.modals.addUser.addSuccess",
    failure: "userManagement.modals.addUser.addFailedFallback",
    pending: "userManagement.modals.addUser.addingUser",
    submit: "userManagement.modals.addUser.addUser",
  },
  edit: {
    title: "userManagement.modals.editUser.title",
    success: "userManagement.modals.editUser.updateSuccess",
    failure: "userManagement.modals.editUser.updateErrorFallback",
    pending: "userManagement.modals.editUser.savingChanges",
    submit: "userManagement.modals.editUser.saveChanges",
  },
} as const;

const UserFormModal: FC<UserFormModalProps> = ({
  isOpen,
  mode,
  user = null,
  apiKey,
  tenantId,
  isSocialListeningEnabled,
  onClose,
  onSubmitSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<UserFormValues>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const texts = MODE_TEXTS[mode];
  const { dashboardOptions, isFetching, changeSelection, resetSelection } = useDashboardOptions({
    form,
    isOpen,
    mode,
    user,
    apiKey,
    tenantId,
  });

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        firstName: user.first_name,
        lastName: user.last_name,
        username: user.name,
        email: user.email,
        role: user.accessLevel.toLowerCase(),
        status: user.isActive ? "active" : "inactive",
        features: user.socialListeningAccess ? [SOCIAL_LISTENING_FEATURE] : [],
      });
    }
  }, [user, form]);

  const updateExistingUser = async (
    existingUser: User,
    values: UserFormValues,
    dashboards: string[],
    socialListeningAccess: boolean
  ): Promise<User> => {
    const isActive = values.status === "active";
    const response = await updateUser(
      apiKey,
      existingUser.id,
      values.firstName,
      values.lastName,
      values.role,
      isActive,
      dashboards.map(id => parseInt(id, 10)),
      socialListeningAccess,
      tenantId
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || t("userManagement.modals.editUser.updateFailedFallback"));
    }

    return {
      ...existingUser,
      name: `${values.firstName} ${values.lastName}`.trim(),
      email: values.email,
      accessLevel: values.role,
      isActive,
      dashboardAccess: dashboards,
      socialListeningAccess,
    };
  };

  const handleSubmit = async (values: UserFormValues) => {
    if (mode === "edit" && !user) {
      return;
    }

    setIsSubmitting(true);
    try {
      const dashboards = (values.dashboardAccess ?? []).filter(id => id !== ALL_DASHBOARDS_VALUE);
      const socialListeningAccess = isSocialListeningEnabled
        ? Boolean(values.features?.includes(SOCIAL_LISTENING_FEATURE))
        : Boolean(user?.socialListeningAccess);

      const savedUser = user
        ? await updateExistingUser(user, values, dashboards, socialListeningAccess)
        : await addUserAPI(values, dashboards, socialListeningAccess, tenantId, apiKey);

      message.success(t(texts.success));
      onSubmitSuccess(savedUser);
      form.resetFields();
      resetSelection();
      onClose();
    } catch (error) {
      const getMessage = mode === "edit" ? GetErrorMessage : GetApiErrorMessage;
      message.error(getMessage(error, t(texts.failure)));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={t(texts.title)}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={600}
      className="add-user-modal"
    >
      <Divider />
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="add-user-form"
        initialValues={mode === "add" ? ADD_INITIAL_VALUES : undefined}
        requiredMark={false}
      >
        <UserFormFields
          mode={mode}
          isSocialListeningEnabled={isSocialListeningEnabled}
          dashboardOptions={dashboardOptions}
          isDashboardsLoading={isFetching}
          onDashboardChange={changeSelection}
        />

        <Form.Item className="form-actions">
          <Button
            type="primary"
            htmlType="submit"
            className="button button--primary"
            disabled={isSubmitting}
            icon={isSubmitting ? <SyncOutlined spin /> : null}
          >
            {t(isSubmitting ? texts.pending : texts.submit)}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UserFormModal;
